import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  listOpportunities, createOpportunity, updateOpportunity,
  openOpportunity, closeOpportunity, cancelOpportunity, deleteOpportunity,
  searchEventsForOpportunity,
  type AdminOpportunity, type SelectableEvent, type OpportunityInput,
  type OfferingType, type SecurityType,
} from '../../lib/admin';
import { ApiError } from '../../lib/apiClient';
import { attachSpvByName, similarNamesFrom, type SimilarSpvNames } from '../../lib/spv';
import { getOfferingLocks, type OfferingLockState } from '../../lib/admin';
import { EligibilityPanel } from './AdminNetwork';
import { formatMoney, formatDate } from '../../lib/format';
import './AdminPages.css';

/**
 * Investment opportunities — the operator's view of every raise.
 *
 * The investor marketplace shows open raises styled as an offer. This screen is the
 * other side of that: every status, the terms behind them, and the two facts that
 * decide what can happen next (is the SPV provisioned, is the event public).
 *
 * The lifecycle is deliberately narrow, and the UI mirrors the engine rather than
 * softening it:
 *
 *   draft  — terms editable, nothing committed, may exist on a non-compliant event
 *   open   — terms frozen, investors can commit
 *   funded/closed — terminal, derived from whether every share sold
 *
 * The one thing worth understanding here: compliance does NOT block CREATING a raise.
 * It blocks OPENING one, because opening is the point money can first be taken.
 * Drafting terms while permits are still pending is ordinary; taking investment
 * against an event that may never happen is not. So a draft on a non-compliant event
 * renders normally, with `blockedReason` naming exactly what is missing.
 */

const BLOCKED_COPY: Record<string, string> = {
  event_compliance_incomplete:
    'The event has not cleared compliance, so it cannot be published, and a raise cannot open on an unpublished event. Clear the checklist under Compliance first.',
  event_not_published:
    'The event has cleared compliance but is still a draft. The organizer needs to publish it.',
  spv_not_provisioned:
    'No Wefunder SPV is attached. This should not happen, creation provisions one.',
  spv_not_attached:
    'No SPV is attached to this event. Net proceeds have nowhere to be paid, so a raise cannot open. Attach one below.',
  spv_onboarding_incomplete:
    'The attached SPV has not finished Stripe onboarding, so it cannot receive a transfer. Finish it under SPV payouts.',
};

const dollars = (cents: number) => (cents / 100).toFixed(2);

/**
 * Attach the SPV that will receive net proceeds for this raise's event, by name.
 *
 * Sits on the opportunity row because that is where the block is felt. The link is
 * actually to the EVENT, not the raise: an event can carry two raises (506b and 506c) and
 * both settle to the same entity, so attaching here attaches for the event and any other
 * raise on it.
 *
 * FREE TEXT, NOT A PICKER. A dropdown can only offer entities that already exist, which
 * put an admin in a dead end at exactly the moment they were trying to open a raise: leave
 * the screen, create the SPV, come back. Typing the name does both in one step.
 *
 * THE OUTCOME IS REPORTED BECAUSE IT IS AMBIGUOUS. The server links an existing company or
 * creates a new one, and nothing in the field itself says which. A typo in a legal name is
 * silent and expensive: it produces a second entity with its own Stripe account, and the
 * first anyone would notice is a payout landing somewhere it should not. So the result says
 * created or linked, and an admin who expected the other one has just caught their typo.
 */
function SpvAttacher({ opp, onChanged }: { opp: AdminOpportunity; onChanged: () => void }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ created: boolean; name: string } | null>(null);
  const [similar, setSimilar] = useState<SimilarSpvNames | null>(null);

  const typed = name.trim();

  async function attach(value: string, confirmCreate = false) {
    const entity = value.trim();
    if (entity.length < 2 || busy) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await attachSpvByName(opp.event.eventId, entity, confirmCreate);
      setResult({ created: res.created, name: res.spv.legalEntityName });
      setSimilar(null);
      setName('');
      onChanged();
    } catch (err) {
      // A 409 here is a question, not a failure: the name is close to something that
      // already exists and the engine will not guess which was meant.
      const ask = similarNamesFrom(err);
      if (ask) setSimilar(ask);
      else setError(err instanceof ApiError ? err.message : 'Could not attach that SPV.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="adm-card__body">
      <h4 className="adm-card__subtitle">Settlement destination</h4>

      {opp.spv ? (
        <p className="adm-hint">
          Net proceeds for <strong>{opp.event.title}</strong> go to{' '}
          <strong>{opp.spv.legalEntityName}</strong>.
          {opp.spv.canReceiveTransfers
            ? ' Ready to receive transfers.'
            : ' Stripe onboarding is not finished, so a raise still cannot open.'}
        </p>
      ) : (
        <p className="adm-hint">
          No SPV is attached to this event. Name the entity that will receive net
          proceeds, then open the raise.
        </p>
      )}

      <div className="adm-actions">
        <input
          type="text"
          className="adm-spv-input"
          value={name}
          disabled={busy}
          placeholder={opp.spv ? 'Replace with a different entity' : 'Legal entity name'}
          aria-label="SPV legal entity name"
          onChange={(e) => { setName(e.target.value); setSimilar(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') void attach(name); }}
        />
        <button type="button" className="button button--primary adm-btn-sm"
          disabled={typed.length < 2 || busy} onClick={() => void attach(name)}>
          {busy ? 'Attaching…' : 'Attach to event'}
        </button>
      </div>

      <p className="adm-spv-help">
        Enter the exact legal name of the Special Purpose Vehicle (SPV) company that will
        receive net proceeds for this event.
      </p>

      {similar && (
        <div className="adm-spv-similar" role="alert">
          <p className="adm-spv-similar__q">
            <strong>{similar.typed}</strong> is close to an entity that already exists.
            Which did you mean?
          </p>
          <div className="adm-actions">
            {similar.candidates.map((c) => (
              <button key={c.id} type="button" className="button button--secondary adm-btn-sm"
                disabled={busy} onClick={() => void attach(c.legalEntityName)}>
                {c.legalEntityName}
              </button>
            ))}
            <button type="button" className="button adm-btn-sm adm-spv-similar__new"
              disabled={busy} onClick={() => void attach(similar.typed, true)}>
              Neither, create {similar.typed}
            </button>
          </div>
          <p className="adm-spv-help">
            Creating it makes a separate company with its own Stripe account and its own
            payout destination.
          </p>
        </div>
      )}

      {result && (
        <p className="adm-hint">
          {result.created
            ? `Created ${result.name} and attached it. If you meant a company that already existed, check the spelling: this is now a separate entity.`
            : `Linked the existing ${result.name}.`}
          {' '}Its Stripe onboarding must be complete before the raise can open.
        </p>
      )}

      {error && <p className="adm-error" role="alert">{error}</p>}
    </div>
  );
}


const centsFrom = (v: string) => Math.round(Number(v) * 100);

function Pill({ status }: { status: string }) {
  return <span className={`adm-pill adm-pill--${status}`}>{status}</span>;
}

/**
 * A refusal, in words an admin can act on.
 *
 * Several engine refusals carry a `details.note` written for exactly this moment: the
 * venue-share guards, for instance, explain that the event already carries a rate and name
 * it. Showing only `err.message` reduces that to a code like
 * `venue_share_already_set_for_event`, which is true and useless.
 */
function explainApiError(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) return fallback;
  const note = (err.payload as { details?: { note?: string } } | undefined)?.details?.note;
  return note ? `${err.message}. ${note}` : err.message;
}

// ── Terms form, shared by create and edit ───────────────────────────────────

interface TermsState {
  title: string;
  description: string;
  totalShares: string;
  pricePerShare: string;
  minimumShares: string;
  gpInvestment: string;
  totalEventCost: string;
  preferredReturn: string;
  gpCarry: string;
  venueShare: string;
  closesAt: string;
  offeringType: OfferingType;
  securityType: SecurityType;
  offeringStartDate: string;
  /** Per-raise hero media. Empty falls back to the event's image. */
  featuredImageUrl: string;
  backgroundVideoUrl: string;
  backgroundVideoPosterUrl: string;
}

const EMPTY_TERMS: TermsState = {
  title: '', description: '',
  totalShares: '500', pricePerShare: '100', minimumShares: '5',
  gpInvestment: '0', totalEventCost: '',
  preferredReturn: '8', gpCarry: '40', venueShare: '', closesAt: '',
  // 506(c) is the default because it is the safer mistake. A raise wrongly marked public
  // is over-disclosed to an audience already entitled to a teaser; one wrongly marked
  // private reaches nobody, and the temporal gate makes that unrecoverable for anyone
  // tagged after it opens.
  offeringType: '506c', securityType: 'equity_units', offeringStartDate: '',
  featuredImageUrl: '', backgroundVideoUrl: '', backgroundVideoPosterUrl: '',
};

const SECURITY_LABEL: Record<SecurityType, string> = {
  equity_units: 'Equity units',
  revenue_share: 'Revenue share',
  convertible_note: 'Convertible note',
  safe: 'SAFE',
};

/**
 * The terms form, in creation and in edit.
 *
 * `locks` is null on the creation screen, where nothing is locked because nothing has
 * been signed. On an existing offering it comes from the engine, so the fields this
 * renders read-only are exactly the fields the server will refuse. The read-only state is
 * a courtesy that stops an honest mistake; the refusal is the rule, and it holds against
 * a direct API call, a stale tab, or a script regardless of what this form shows.
 */
function TermsFields({
  terms, set, disabled, locks = null,
}: {
  terms: TermsState;
  set: (patch: Partial<TermsState>) => void;
  disabled: boolean;
  locks?: OfferingLockState | null;
}) {
  // Frozen by the first signature: these are the terms of an executed agreement.
  const signed = locks?.hasSignedInvestor === true;
  // Frozen only once settlement has consumed it.
  const settled = locks?.hasSettled === true;
  // The engine requires investorPool === totalShares × pricePerShare, so the pool is
  // never an input here — it is shown as the consequence of the two factors above it.
  const poolCents = centsFrom(terms.pricePerShare) * (Number(terms.totalShares) || 0);
  const gpCents = centsFrom(terms.gpInvestment);
  const costCents = centsFrom(terms.totalEventCost);
  const overBudget = costCents > 0 && poolCents + gpCents > costCents;

  return (
    <div className="opp-form">
      {/* THE REGULATORY BASIS COMES FIRST, before any number, because it decides who may
          ever see this raise and it cannot be changed once the raise is open. */}
      <fieldset className="opp-form__wide opp-reg">
        <legend className="field__label">Offering type *</legend>

        <label className={terms.offeringType === '506c' ? 'opp-reg__opt is-on' : 'opp-reg__opt'}>
          <input type="radio" name="offeringType" value="506c" disabled={disabled}
            checked={terms.offeringType === '506c'}
            onChange={() => set({ offeringType: '506c' })} />
          <span>
            <strong>Rule 506(c), public</strong>
            <span className="opp-reg__hint">
              Can be advertised. Appears in the public marketplace as a teaser once opened,
              and is open to any verified accredited investor.
            </span>
          </span>
        </label>

        <label className={terms.offeringType === '506b' ? 'opp-reg__opt is-on' : 'opp-reg__opt'}>
          <input type="radio" name="offeringType" value="506b" disabled={disabled}
            checked={terms.offeringType === '506b'}
            onChange={() => set({ offeringType: '506b' })} />
          <span>
            <strong>Rule 506(b), private network</strong>
            <span className="opp-reg__hint">
              Cannot be advertised anywhere. Only reaches investors whose relationship with
              Voleto was recorded <em>before</em> this offering starts.
            </span>
          </span>
        </label>

        {terms.offeringType === '506b' && (
          <p className="opp-reg__warn" role="note">
            <strong>Tag your investors before you open this raise.</strong> Opening stamps
            the offering start date, and the gate admits only relationships recorded
            before it. Anyone tagged afterwards is shut out of this raise permanently, with
            no way to undo it. Check who qualifies on the 506(b) network screen first.
          </p>
        )}
      </fieldset>

      <label className="field">
        <span className="field__label">Security type *</span>
          {signed && <span className="field__hint adm-locked">Locked: an investor has signed against these terms.</span>}
        <select className="field__input" value={terms.securityType} disabled={disabled || signed}
          onChange={(e) => set({ securityType: e.target.value as SecurityType })}>
          {(Object.keys(SECURITY_LABEL) as SecurityType[]).map((k) => (
            <option key={k} value={k}>{SECURITY_LABEL[k]}</option>
          ))}
        </select>
      </label>

      <label className="field">
        <span className="field__label">Offering start date</span>
          {signed && <span className="field__hint adm-locked">Locked: an investor has signed against these terms.</span>}
        <input className="field__input" type="date" value={terms.offeringStartDate}
          disabled={disabled || signed}
          onChange={(e) => set({ offeringStartDate: e.target.value })} />
        <span className="opp-reg__hint">
          Leave empty to stamp it when you open the raise, which is usually what you want.
          Set it only to record a date solicitation genuinely began.
        </span>
      </label>

      <label className="field">
        <span className="field__label">Title *</span>
        <input className="field__input" type="text" value={terms.title} disabled={disabled}
          onChange={(e) => set({ title: e.target.value })}
          placeholder="Back the Nocturne tour" />
      </label>

      <label className="field opp-form__wide">
        <span className="field__label">Description</span>
        <textarea className="field__input" rows={3} value={terms.description} disabled={disabled}
          onChange={(e) => set({ description: e.target.value })}
          placeholder="What the money funds, and what the investor is buying into." />
      </label>

      <label className="field">
        <span className="field__label">Total shares *</span>
          {signed && <span className="field__hint adm-locked">Locked: an investor has signed against these terms.</span>}
        <input className="field__input mono" type="number" min="1" step="1"
          value={terms.totalShares} disabled={disabled || signed}
          onChange={(e) => set({ totalShares: e.target.value })} />
      </label>

      <label className="field">
        <span className="field__label">Price per share (USD) *</span>
          {signed && <span className="field__hint adm-locked">Locked: an investor has signed against these terms.</span>}
        <input className="field__input mono" type="number" min="0.01" step="0.01"
          value={terms.pricePerShare} disabled={disabled || signed}
          onChange={(e) => set({ pricePerShare: e.target.value })} />
      </label>

      <label className="field">
        <span className="field__label">Minimum shares per investor *</span>
        <input className="field__input mono" type="number" min="1" step="1"
          value={terms.minimumShares} disabled={disabled}
          onChange={(e) => set({ minimumShares: e.target.value })} />
      </label>

      <div className="opp-derived">
        <span className="field__label">Investor pool</span>
        <span className="opp-derived__value mono">{formatMoney(poolCents)}</span>
        <span className="adm-hint adm-muted">
          Derived as shares × price. Not an input, the engine rejects any other value.
        </span>
      </div>

      <label className="field">
        <span className="field__label">Manager capital contribution (USD)</span>
          {signed && <span className="field__hint adm-locked">Locked: an investor has signed against these terms.</span>}
          <span className="field__hint">
            What this raise is sized for: capital the Manager puts in alongside the investor
            pool, so the two together fund the production. It is a plan, set before anyone
            has invested. The contribution itself is recorded as a real Member position and
            shown below as contributed.
          </span>
        <input className="field__input mono" type="number" min="0" step="0.01"
          value={terms.gpInvestment} disabled={disabled || signed}
          onChange={(e) => set({ gpInvestment: e.target.value })} />
      </label>

      <label className="field">
        <span className="field__label">Total event cost (USD) *</span>
          {settled && <span className="field__hint adm-locked">Locked: settlement has run for this event.</span>}
          {!settled && signed && <span className="field__hint">Editable, and every change is recorded in the audit log with its before and after value.</span>}
        <input className="field__input mono" type="number" min="0" step="0.01"
          value={terms.totalEventCost} disabled={disabled || settled}
          onChange={(e) => set({ totalEventCost: e.target.value })} />
      </label>

      <label className="field">
        <span className="field__label">Preferred return (%)</span>
          {signed && <span className="field__hint adm-locked">Locked: an investor has signed against these terms.</span>}
        <input className="field__input mono" type="number" min="0" step="0.1"
          value={terms.preferredReturn} disabled={disabled || signed}
          onChange={(e) => set({ preferredReturn: e.target.value })} />
      </label>

      <label className="field">
        <span className="field__label">Manager promote (%)</span>
          {signed && <span className="field__hint adm-locked">Locked: an investor has signed against these terms.</span>}
        <input className="field__input mono" type="number" min="0" step="0.1" max="100"
          value={terms.gpCarry} disabled={disabled || signed}
          onChange={(e) => set({ gpCarry: e.target.value })} />
        <span className="field__hint">
          The Manager&apos;s share of profit ABOVE the hurdle, never of gross. Return of capital
          and the preferred return are both paid in full first.
        </span>
      </label>

      {/* ── Venue revenue share ──────────────────────────────────────────────────────
          A deal term set here, but STORED ON THE EVENT. The venue is paid once out of one
          pool of ticket revenue before anything splits, so an event carries one rate
          however many raises it has. The engine refuses a second raise asking for a
          different one, and refuses any rate at all unless the event has a confirmed venue
          that can actually receive a transfer. */}
      <label className="field">
        <span className="field__label">Venue revenue share (%)</span>
          {signed && <span className="field__hint adm-locked">Locked: an investor has signed against these terms.</span>}
        <input className="field__input mono" type="number" min="0" max="100" step="0.01"
          value={terms.venueShare} disabled={disabled || signed}
          placeholder="none"
          onChange={(e) => set({ venueShare: e.target.value })} />
        <span className="field__hint">
          For a venue taking a percentage of ticketing revenue instead of a fee up front.
          Charged on gross LESS card processing and the platform fee, before net proceeds.
          Leave empty for no arrangement.
        </span>
        <span className="field__hint">
          Applies to the WHOLE EVENT, not only this raise, and requires a confirmed venue
          booking whose payout account can receive transfers. Without one the deduction
          would have nobody to pay.
        </span>
      </label>

      {/* ── Property media ───────────────────────────────────────────────────────────
          On the RAISE, not the event. One event can carry more than one raise, and
          hanging the hero off the event would give both the same image with no way to
          tell them apart on an investor's dashboard. */}
      <label className="field">
        <span className="field__label">Hero image URL</span>
        <input className="field__input" type="url" value={terms.featuredImageUrl}
          disabled={disabled} placeholder="https://..."
          onChange={(e) => set({ featuredImageUrl: e.target.value })} />
        <span className="field__hint">
          Shown on this raise&apos;s dashboard. Leave empty to fall back to the
          event&apos;s image.
        </span>
      </label>

      <label className="field">
        <span className="field__label">Background video URL</span>
        <input className="field__input" type="url" value={terms.backgroundVideoUrl}
          disabled={disabled} placeholder="https://... .mp4"
          onChange={(e) => set({ backgroundVideoUrl: e.target.value })} />
      </label>

      <label className="field">
        <span className="field__label">Video poster image</span>
        <input className="field__input" type="url" value={terms.backgroundVideoPosterUrl}
          disabled={disabled} placeholder="https://... .jpg"
          onChange={(e) => set({ backgroundVideoPosterUrl: e.target.value })} />
        {/* Not a nicety. Autoplay is refused by iOS Low Power Mode, by Data Saver, and by
            anything that strips the muted attribute; the poster is what those cases
            paint. A video with no poster is a blank hero on most phones. */}
        {terms.backgroundVideoUrl.trim() && !terms.backgroundVideoPosterUrl.trim() && (
          <span className="field__hint adm-warn">
            A video with no poster renders as an empty hero wherever autoplay is blocked,
            which is most phones. Set one.
          </span>
        )}
        {!terms.backgroundVideoUrl.trim() && (
          <span className="field__hint">
            Shown wherever autoplay is refused. Only needed if a video is set.
          </span>
        )}
      </label>

      <label className="field">
        <span className="field__label">Closes at</span>
        <input className="field__input" type="date" value={terms.closesAt} disabled={disabled}
          onChange={(e) => set({ closesAt: e.target.value })} />
      </label>

      {overBudget && (
        <p className="adm-error opp-form__wide" role="alert">
          The investor pool plus the GP investment ({formatMoney(poolCents + gpCents)})
          exceeds the total event cost ({formatMoney(costCents)}). The engine will refuse this.
        </p>
      )}
    </div>
  );
}

function toInput(eventId: string, t: TermsState): OpportunityInput {
  const totalShares = Number(t.totalShares);
  const pricePerShareCents = centsFrom(t.pricePerShare);
  return {
    eventId,
    title: t.title.trim(),
    description: t.description.trim() || null,
    totalShares,
    pricePerShareCents,
    investorPoolCents: totalShares * pricePerShareCents,
    managerCapitalContributionCents: centsFrom(t.gpInvestment || '0'),
    totalEventCostCents: centsFrom(t.totalEventCost),
    minimumShares: Number(t.minimumShares),
    preferredReturnPercentage: Number(t.preferredReturn),
    gpCarryPercentage: Number(t.gpCarry),
    // undefined when untouched, so an omitted term leaves the event alone. Empty string
    // means the admin cleared it, which is a real instruction and sends null.
    venueRevenueSharePercentage: t.venueShare.trim() === '' ? null : Number(t.venueShare),
    closesAt: t.closesAt ? new Date(`${t.closesAt}T23:59:59`).toISOString() : null,
    offeringType: t.offeringType,
    securityType: t.securityType,
    // Empty means "stamp it when the raise opens", which is what the engine does.
    offeringStartDate: t.offeringStartDate
      ? new Date(`${t.offeringStartDate}T00:00:00`).toISOString()
      : null,
    // Null rather than empty string, so "cleared" and "never set" are the same state in
    // the database and the fallback to the event's image works for both.
    featuredImageUrl: t.featuredImageUrl.trim() || null,
    backgroundVideoUrl: t.backgroundVideoUrl.trim() || null,
    backgroundVideoPosterUrl: t.backgroundVideoPosterUrl.trim() || null,
  };
}

/** Edit sends terms only — eventId and the derived pool are not editable, and
 *  including them would put fields in the audit entry that were never changed. */
function toEditPatch(t: TermsState): Partial<OpportunityInput> {
  const { eventId: _eventId, investorPoolCents: _pool, ...terms } = toInput('', t);
  return terms;
}

// ── Creation ────────────────────────────────────────────────────────────────

function CreatePanel({ onCreated, onCancel }: { onCreated: () => void; onCancel: () => void }) {
  const [events, setEvents] = useState<SelectableEvent[]>([]);
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<SelectableEvent | null>(null);
  const [terms, setTerms] = useState<TermsState>(EMPTY_TERMS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback((q: string) => {
    searchEventsForOpportunity(q || undefined)
      .then(setEvents)
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Could not load events.'));
  }, []);

  useEffect(() => { load(''); }, [load]);

  const set = (patch: Partial<TermsState>) => setTerms((t) => ({ ...t, ...patch }));

  async function submit() {
    if (!picked) return;
    setBusy(true);
    setError(null);
    try {
      await createOpportunity(toInput(picked.eventId, terms));
      onCreated();
    } catch (err) {
      // The venue-share refusals carry a `note` explaining what to do about them, which is
      // more use than the bare code. Surfaced where the engine provides one.
      setError(explainApiError(err, 'Could not create the opportunity.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="adm-card opp-create">
      <header className="adm-card__head">
        <h2 className="adm-card__title">New investment opportunity</h2>
        <button type="button" className="button button--ghost adm-btn-sm" onClick={onCancel}>
          Cancel
        </button>
      </header>

      <div className="adm-card__body">
        {error && <p className="adm-error" role="alert">{error}</p>}

        {!picked ? (
          <>
            <p className="adm-section__note adm-muted">
              Pick the event this raise funds. Events that have not cleared compliance are
              listed, a raise may be drafted against them, it just cannot open until the
              event is published.
            </p>
            <form className="adm-filters" onSubmit={(e) => { e.preventDefault(); load(search); }}>
              <input className="field__input adm-search" type="search" value={search}
                placeholder="Event, city, or organizer email"
                onChange={(e) => setSearch(e.target.value)} />
              <button type="submit" className="button button--secondary">Search</button>
            </form>

            <div className="adm-table-wrap">
              <table className="adm-table adm-table--dense">
                <thead>
                  <tr>
                    <th>Event</th><th>Date</th><th>Status</th>
                    <th>Compliance</th><th>Existing raise</th><th />
                  </tr>
                </thead>
                <tbody>
                  {events.map((ev) => (
                    <tr key={ev.eventId}>
                      <td>
                        <div className="adm-user">
                          <span className="adm-user__name">{ev.title}</span>
                          <span className="adm-user__email">
                            {ev.venue ?? ' | '} · {ev.organizer.email}
                          </span>
                        </div>
                      </td>
                      <td className="mono adm-nowrap">
                        {ev.eventDate ? formatDate(ev.eventDate) : ' | '}
                      </td>
                      <td><Pill status={ev.status} /></td>
                      <td>
                        {ev.overallCompliant
                          ? <span className="adm-pill adm-pill--approved">clear</span>
                          : <span className="adm-muted adm-hint">incomplete</span>}
                      </td>
                      <td>
                        {ev.existingOpportunityStatus
                          ? <Pill status={ev.existingOpportunityStatus} />
                          : <span className="adm-muted"> | </span>}
                      </td>
                      <td>
                        <button type="button" className="button button--secondary adm-btn-sm"
                          onClick={() => {
                            setPicked(ev);
                            set({ title: `Back ${ev.title}` });
                          }}>
                          Select
                        </button>
                      </td>
                    </tr>
                  ))}
                  {events.length === 0 && (
                    <tr><td colSpan={6} className="adm-muted">No events match.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <>
            <div className="opp-picked">
              <div>
                <span className="adm-user__name">{picked.title}</span>
                <span className="adm-user__email"> · {picked.venue ?? ' | '}</span>
              </div>
              <button type="button" className="button button--ghost adm-btn-sm"
                onClick={() => setPicked(null)}>
                Change event
              </button>
            </div>

            {!picked.canOpenOnceCreated && (
              <p className="adm-flash" role="status">
                This raise will be created as a <strong>draft</strong> and cannot be opened
                yet: {picked.overallCompliant
                  ? 'the event is not published.'
                  : 'the event has not cleared compliance.'} That is expected, draft the
                terms now, open once the event is live.
              </p>
            )}

            <TermsFields terms={terms} set={set} disabled={busy} />

            <div className="sup-prompt__actions">
              <button type="button" className="button button--ghost" onClick={onCancel} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="button button--primary" onClick={submit}
                disabled={busy || !terms.title.trim() || !terms.totalEventCost}>
                {busy ? 'Creating…' : 'Create draft'}
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

// ── One row in the list ─────────────────────────────────────────────────────

function OpportunityCard({ opp, onChanged }: { opp: AdminOpportunity; onChanged: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState<'open' | 'close' | 'cancel' | 'delete' | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [terms, setTerms] = useState<TermsState>(EMPTY_TERMS);
  const [locks, setLocks] = useState<OfferingLockState | null>(null);

  const sold = opp.totalShares > 0 ? Math.round((opp.sharesSold / opp.totalShares) * 100) : 0;

  async function startEdit() {
    // Fetched before the form opens, so the read-only state is correct on first paint
    // rather than after a flash of editable fields.
    try { setLocks(await getOfferingLocks(opp.id)); } catch { setLocks(null); }
    setTerms({
      title: opp.title,
      description: opp.description ?? '',
      totalShares: String(opp.totalShares),
      pricePerShare: dollars(opp.pricePerShareCents),
      minimumShares: String(opp.minimumShares),
      gpInvestment: dollars(opp.managerCapitalContributionCents),
      totalEventCost: dollars(opp.totalEventCostCents),
      preferredReturn: String(opp.preferredReturnPercentage),
      gpCarry: String(opp.gpCarryPercentage),
      venueShare: opp.venueRevenueSharePercentage != null ? String(opp.venueRevenueSharePercentage) : '',
      featuredImageUrl: opp.featuredImageUrl ?? '',
      backgroundVideoUrl: opp.backgroundVideoUrl ?? '',
      backgroundVideoPosterUrl: opp.backgroundVideoPosterUrl ?? '',
      closesAt: opp.closesAt ? opp.closesAt.slice(0, 10) : '',
      // Prefilled from the existing raise, so editing a draft cannot silently reset its
      // regulatory basis to the 506(c) default.
      offeringType: opp.offeringType === '506b' ? '506b' : '506c',
      securityType: (opp.securityType ?? 'equity_units') as SecurityType,
      offeringStartDate: opp.offeringStartDate ? opp.offeringStartDate.slice(0, 10) : '',
    });
    setEditing(true);
    setExpanded(true);
  }

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setEditing(false);
      setConfirm(null);
      setReason('');
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The action failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className={`adm-card${opp.blockedReason ? ' adm-card--blocked' : ''}`}>
      <header className="adm-card__head">
        <div className="adm-card__title-wrap">
          <button type="button" className="adm-disclosure" onClick={() => setExpanded((e) => !e)}>
            <span className="adm-disclosure__caret">{expanded ? '▾' : '▸'}</span>
            <span className="adm-card__title">{opp.title}</span>
          </button>
          <p className="adm-card__meta">
            {opp.event.title} · {opp.event.date ? formatDate(opp.event.date) : 'no date'}
            {opp.event.city ? ` · ${opp.event.city}` : ''}
          </p>
        </div>
        <div className="adm-card__status">
          <Pill status={opp.status} />
          <span className="adm-hint adm-muted mono">
            {opp.sharesSold}/{opp.totalShares} shares · {formatMoney(opp.committedCents)}
          </span>
        </div>
      </header>

      {expanded && (
        <div className="adm-card__body">
          {error && <p className="adm-error" role="alert">{error}</p>}

          {opp.status === 'draft' && (
            <SpvAttacher opp={opp} onChanged={onChanged} />
          )}

          {opp.blockedReason && (
            <p className="adm-flash" role="status">
              <strong className="adm-warn">Cannot open yet.</strong>{' '}
              {BLOCKED_COPY[opp.blockedReason] ?? opp.blockedReason}
            </p>
          )}

          {editing ? (
            <>
              <TermsFields terms={terms} set={(p) => setTerms((t) => ({ ...t, ...p }))}
                disabled={busy} locks={locks} />
              <div className="sup-prompt__actions">
                <button type="button" className="button button--ghost" disabled={busy}
                  onClick={() => setEditing(false)}>Cancel</button>
                <button type="button" className="button button--primary" disabled={busy}
                  onClick={() => run(() =>
                    updateOpportunity(opp.id, toEditPatch(terms)))}>
                  {busy ? 'Saving…' : 'Save terms'}
                </button>
              </div>
            </>
          ) : (
            <>
              <dl className="sup-facts">
                <div><dt>Investor pool</dt><dd className="mono">{formatMoney(opp.investorPoolCents)}</dd></div>
                <div><dt>Price/share</dt><dd className="mono">{formatMoney(opp.pricePerShareCents)}</dd></div>
                <div><dt>Minimum</dt><dd className="mono">{opp.minimumShares} shares</dd></div>
                <div><dt>Sold</dt><dd className="mono">{sold}%</dd></div>
                {/* Two numbers, deliberately. The Manager can hold a Member position in
                    its own raise, and folding it into one "investors" figure would show a
                    prospect the platform's own co-investment as another party who chose to
                    invest. Positions is only shown when it differs, so the ordinary case
                    stays one line. */}
                <div><dt>Investors</dt><dd className="mono">{opp.investors}</dd></div>
                {opp.memberPositionCount !== opp.investors && (
                  <div>
                    <dt>Member positions</dt>
                    <dd className="mono">{opp.memberPositionCount} <span className="adm-hint">incl. Manager</span></dd>
                  </div>
                )}
                <div><dt>Event cost</dt><dd className="mono">{formatMoney(opp.totalEventCostCents)}</dd></div>
                <div>
                  <dt>Manager capital</dt>
                  {/* Planned against contributed. A raise sized for one figure that
                      received another has to show the gap, not average it away. */}
                  <dd className="mono">
                    {formatMoney(opp.managerCapitalActualCents)}
                    {opp.managerCapitalActualCents !== opp.gpInvestmentCents && (
                      <span className="adm-hint"> of {formatMoney(opp.gpInvestmentCents)} planned</span>
                    )}
                  </dd>
                </div>
                <div><dt>Pref. return</dt><dd className="mono">{opp.preferredReturnPercentage}%</dd></div>
                <div><dt>Perf. fee</dt><dd className="mono">{opp.performanceFeePercentage}%</dd></div>
                <div><dt>Closes</dt><dd className="mono">{opp.closesAt ? formatDate(opp.closesAt) : ' | '}</dd></div>
                <div><dt>SPV</dt><dd className="mono adm-hint">{opp.wefunderSpvId ?? ' | '}</dd></div>
              </dl>

              {opp.description && <p className="adm-bio">{opp.description}</p>}

              {confirm === 'open' && (
                <div className="sup-prompt">
                  <h4 className="sup-prompt__title">Open this raise to investors?</h4>
                  <div className="sup-prompt__body">
                    <ul className="sup-consequences">
                      <li>It appears in the <strong>investor marketplace</strong> immediately.</li>
                      <li>The gated card on the public event page starts showing financials
                        to <strong>approved investors</strong>.</li>
                      <li>The terms <strong>freeze</strong>, they cannot be edited afterwards,
                        because investors commit against them.</li>
                      {opp.offeringType === '506b' && (
                        <li>
                          Opening stamps the <strong>offering start date</strong>. Under Rule
                          506(b) only investors whose relationship <em>predates</em> that
                          moment may ever see this deal, anyone tagged afterwards is
                          excluded from it permanently.
                        </li>
                      )}
                    </ul>
                  </div>

                  {/* The impact, before committing. For a 506(b) raise this is the last
                      point at which an untagged investor can still be brought in, after
                      opening, that door is shut for this deal for good. */}
                  {opp.offeringType === '506b' && (
                    <div className="sup-prompt__body">
                      <EligibilityPanel offeringId={opp.id} />
                    </div>
                  )}
                  <div className="sup-prompt__actions">
                    <button type="button" className="button button--ghost" disabled={busy}
                      onClick={() => setConfirm(null)}>Cancel</button>
                    <button type="button" className="button button--primary" disabled={busy}
                      onClick={() => run(() => openOpportunity(opp.id))}>
                      {busy ? 'Opening…' : 'Open raise'}
                    </button>
                  </div>
                </div>
              )}

              {confirm === 'close' && (
                <div className="sup-prompt">
                  <h4 className="sup-prompt__title">Close this raise?</h4>
                  <div className="sup-prompt__body">
                    <p>
                      {opp.sharesSold >= opp.totalShares
                        ? <>Every share sold, so this closes as <strong>funded</strong>.</>
                        : <><strong>{opp.sharesSold} of {opp.totalShares}</strong> shares sold,
                          so this closes as <strong>closed</strong>, under-subscribed.</>}
                    </p>
                    <p>It disappears from the investor marketplace. This is not reversible.</p>
                  </div>
                  <label className="field">
                    <span className="field__label">Reason (recorded in the audit log)</span>
                    <input className="field__input" type="text" value={reason} autoFocus
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Deadline reached" />
                  </label>
                  <div className="sup-prompt__actions">
                    <button type="button" className="button button--ghost" disabled={busy}
                      onClick={() => setConfirm(null)}>Cancel</button>
                    <button type="button" className="button button--ghost sup-danger" disabled={busy}
                      onClick={() => run(() => closeOpportunity(opp.id, reason.trim() || undefined))}>
                      {busy ? 'Closing…' : 'Close raise'}
                    </button>
                  </div>
                </div>
              )}

              {confirm === 'cancel' && (
                <div className="sup-prompt">
                  <h4 className="sup-prompt__title">Withdraw this raise?</h4>
                  <div className="sup-prompt__body">
                    <ul className="sup-consequences">
                      <li>The status becomes <strong>cancelled</strong> and it stops being
                        an offer to anybody.</li>
                      <li>It leaves the investor marketplace immediately.</li>
                      <li>The record <strong>stays</strong>, with its terms and its history,
                        so the console can still say what was offered and when.</li>
                    </ul>
                    <p>This is not reversible: a cancelled raise cannot be reopened.</p>
                  </div>
                  <label className="field">
                    <span className="field__label">Reason (recorded in the audit log)</span>
                    <input className="field__input" type="text" value={reason} autoFocus
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Production postponed" />
                  </label>
                  <div className="sup-prompt__actions">
                    <button type="button" className="button button--ghost" disabled={busy}
                      onClick={() => setConfirm(null)}>Keep it</button>
                    <button type="button" className="button button--ghost sup-danger" disabled={busy}
                      onClick={() => run(() => cancelOpportunity(opp.id, reason.trim() || undefined))}>
                      {busy ? 'Cancelling…' : 'Cancel raise'}
                    </button>
                  </div>
                </div>
              )}

              {confirm === 'delete' && (
                <div className="sup-prompt">
                  <h4 className="sup-prompt__title">Delete this draft permanently?</h4>
                  <div className="sup-prompt__body">
                    <p>
                      <strong>{opp.title}</strong> is a draft that was never opened, has no
                      investors and nobody registered interest in it. Deleting removes the
                      record entirely.
                    </p>
                    {/* The distinction that matters on this screen, said plainly, because
                        the two buttons sit next to each other and only one is reversible
                        in the sense of leaving evidence behind. */}
                    <p className="adm-hint">
                      If you want the raise to stop existing as an offer but still want a
                      record of it, cancel it instead. The audit log keeps a note of this
                      deletion either way.
                    </p>
                  </div>
                  <div className="sup-prompt__actions">
                    <button type="button" className="button button--ghost" disabled={busy}
                      onClick={() => setConfirm(null)}>Keep it</button>
                    <button type="button" className="button button--ghost sup-danger" disabled={busy}
                      onClick={() => run(() => deleteOpportunity(opp.id))}>
                      {busy ? 'Deleting…' : 'Delete permanently'}
                    </button>
                  </div>
                </div>
              )}

              {!confirm && (
                <div className="sup-actions">
                  {/* Draft OR open. Open raises are editable under the locks the engine
                      reports; what is frozen depends on whether anyone has signed, not on
                      the raise's status. */}
                  {(opp.canEdit || opp.status === 'open') && (
                    <button type="button" className="button button--secondary adm-btn-sm"
                      onClick={() => void startEdit()}>Edit terms</button>
                  )}
                  {opp.canOpen && (
                    <button type="button" className="button button--primary adm-btn-sm"
                      onClick={() => setConfirm('open')}>Open to investors</button>
                  )}
                  {opp.canClose && (
                    <button type="button" className="button button--ghost adm-btn-sm sup-danger"
                      onClick={() => setConfirm('close')}>Close raise</button>
                  )}
                  {opp.canCancel && (
                    <button type="button" className="button button--ghost adm-btn-sm sup-danger"
                      onClick={() => setConfirm('cancel')}>Cancel raise</button>
                  )}
                  {opp.canDelete && (
                    <button type="button" className="button button--ghost adm-btn-sm sup-danger"
                      onClick={() => setConfirm('delete')}>Delete draft</button>
                  )}
                  {/* Why the delete button is missing on a draft that looks deletable.
                      Without this the absence reads as a bug rather than a rule. */}
                  {opp.status === 'draft' && !opp.canDelete && (
                    <span className="adm-hint adm-muted">
                      {opp.memberPositionCount > 0
                        ? 'Has investments, so it can only be cancelled.'
                        : 'Someone registered interest, so it can only be cancelled.'}
                    </span>
                  )}
                  {/* Preview the public deal page WITHOUT opening the raise. Opening a
                      506(c) offering is a real public securities solicitation, so it must
                      never be something done merely to look at a layout. Offered only for
                      506(c): a private offering has no public view to preview. */}
                  {opp.offeringType !== '506b' && (
                    <Link className="adm-link adm-btn-sm"
                      to={`/admin/opportunities/${opp.id}/preview`}>
                      Preview as public would see it
                    </Link>
                  )}
                  <a className="adm-link adm-btn-sm" href={`/events/${opp.event.eventId}`}
                    target="_blank" rel="noreferrer">
                    View public event page ↗
                  </a>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </article>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

const TABS = ['all', 'draft', 'open', 'funded', 'closed'] as const;

export function AdminOpportunities() {
  const [opps, setOpps] = useState<AdminOpportunity[]>([]);
  const [tab, setTab] = useState<(typeof TABS)[number]>('all');
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    listOpportunities(tab === 'all' ? undefined : tab)
      .then((r) => { setOpps(r); setError(null); })
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Could not load opportunities.'))
      .finally(() => setLoading(false));
  }, [tab]);

  useEffect(load, [load]);

  const raised = useMemo(
    () => opps.reduce((sum, o) => sum + o.committedCents, 0), [opps]);

  return (
    <div className="adm-page">
      <header className="adm-header">
        <div>
          <h1 className="adm-title">Investment opportunities</h1>
          <p className="adm-lede">
            Every raise, every status. Investors only ever see the open ones, and only
            approved investors see the financials behind them.
          </p>
        </div>
        {!creating && (
          <button type="button" className="button button--primary"
            onClick={() => setCreating(true)}>
            New opportunity
          </button>
        )}
      </header>

      {creating && (
        <CreatePanel
          onCancel={() => setCreating(false)}
          onCreated={() => { setCreating(false); setTab('all'); load(); }}
        />
      )}

      <div className="adm-filters">
        {TABS.map((t) => (
          <button key={t} type="button"
            className={`adm-tab${tab === t ? ' is-active' : ''}`}
            onClick={() => setTab(t)}>
            {t}
          </button>
        ))}
        <span className="adm-hint adm-muted mono">
          {opps.length} shown · {formatMoney(raised)} committed
        </span>
      </div>

      {error && <p className="adm-status adm-error" role="alert">{error}</p>}
      {loading && <p className="adm-status">Loading…</p>}
      {!loading && !error && opps.length === 0 && (
        <p className="adm-status adm-muted">No opportunities with this status.</p>
      )}

      <div className="adm-list">
        {opps.map((o) => (
          <OpportunityCard key={o.id} opp={o} onChanged={load} />
        ))}
      </div>
    </div>
  );
}
